import {activate, isValidationFailedResponse, ValidationFailures} from '@/api';
import FormControls from '@/components/FormControls.tsx';
import Title from '@/components/Title.tsx';
import ValidatedFormGroup from '@/components/ValidatedFormGroup.tsx';
import {Toaster, ToastErrors} from '@/toaster.ts';
import {Button, FormGroup, H2, InputGroup} from '@blueprintjs/core';
import {
	ChangeEventHandler,
	EventHandler,
	ReactElement,
	ReactNode,
	SyntheticEvent,
	useCallback,
	useMemo,
	useState,
} from 'react';
import {useNavigate, useSearchParams} from 'react-router';

interface Props {
	title: string,
	successMessage: string,
	children: ReactNode,
}

export default function PasswordPage({title, successMessage, children}: Props): ReactElement {
	const navigate = useNavigate();

	const [urlParams] = useSearchParams();
	const token = useMemo(() => {
		const token = urlParams.get('token');

		if (!token)
			throw new Error('The `token` URL parameter must contain a JWT');

		return token;
	}, [urlParams]);

	const [processing, setProcessing] = useState(false);

	const [password, setPassword] = useState('');
	const [passwordConfirmation, setPasswordConfirmation] = useState('');

	const onPasswordChange: ChangeEventHandler<HTMLInputElement> = useCallback(event => {
		setPassword(event.currentTarget.value);
	}, []);

	const onPasswordConfirmationChange: ChangeEventHandler<HTMLInputElement> = useCallback(event => {
		setPasswordConfirmation(event.currentTarget.value);
	}, []);

	const [validationFailures, setValidationFailures] = useState<ValidationFailures | null>(null);

	const onSubmit: EventHandler<SyntheticEvent> = useCallback(async event => {
		event.preventDefault();

		if (processing)
			return;

		if (password.length === 0) {
			Toaster.error('Please enter a password.');
			return;
		} else if (password !== passwordConfirmation) {
			Toaster.error('Your password confirmation does not match your password.');
			return;
		}

		setProcessing(true);

		try {
			await activate(password, token);
		} catch (error) {
			if (isValidationFailedResponse(error)) {
				setValidationFailures(error.error.context.failures);
				Toaster.error(error.error.message);
			} else
				ToastErrors.fromError(error);

			return;
		} finally {
			setProcessing(false);
		}

		Toaster.success(successMessage);
		navigate('/login');
	}, [processing, password, passwordConfirmation, token]);

	return (
		<div>
			<Title title={title} />

			{children}

			<form onSubmit={onSubmit}>
				<ValidatedFormGroup label="Password" labelFor="password" failures={validationFailures}>
					<InputGroup type="password" value={password} onChange={onPasswordChange} />
				</ValidatedFormGroup>

				<FormGroup label="Confirm Password">
					<InputGroup type="password" value={passwordConfirmation} onChange={onPasswordConfirmationChange} />
				</FormGroup>

				<FormControls>
					<Button type="submit" intent="primary" text="Submit" loading={processing} />
				</FormControls>
			</form>
		</div>
	)
}
